/* Blotters: positions, orders, executions, depth, time & sales.
 *
 * These are the tables a trader watches all day. Numbers are tabular, P&L sign
 * is explicit as well as coloured, and every table scrolls inside its own
 * container so the page itself never scrolls.
 */
import { useMemo } from 'react'
import { useStore } from '@/store'
import { direction, fmtMoney, fmtPrice, fmtQty, fmtSigned, fmtTime } from '@/lib/format'
import type { Order, Position } from '@/types'
import {
  ExecutionsTable, MarketDepth, StrategiesTable, TimeAndSales,
} from '@/panels/BlotterTables'

const TABS = [
  { id: 'positions', label: 'Positions' },
  { id: 'orders', label: 'Orders' },
  { id: 'executions', label: 'Executions' },
  { id: 'timesales', label: 'Time & Sales' },
  { id: 'depth', label: 'Depth' },
  { id: 'strategies', label: 'Strategies' },
] as const

export function Blotters() {
  const active = useStore((s) => s.activeBlotter)
  const setActive = useStore((s) => s.setActiveBlotter)
  const orders = useStore((s) => s.orders)
  const positions = useStore((s) => s.positions)
  const executions = useStore((s) => s.executions)
  const strategies = useStore((s) => s.strategies)

  return (
    <section style={{
      display: 'flex', flexDirection: 'column', minHeight: 0,
      background: 'var(--bg-panel)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)', overflow: 'hidden',
    }}>
      <nav
        role="tablist"
        style={{
          display: 'flex', gap: 0, height: 30, flexShrink: 0,
          borderBottom: '1px solid var(--border)', background: 'var(--bg-raised)',
        }}
      >
        {TABS.map((t) => {
          const count = t.id === 'orders' ? orders.length
                      : t.id === 'positions' ? positions.length
                      : t.id === 'executions' ? executions.length
                      : t.id === 'strategies' ? strategies.length : 0
          const on = active === t.id
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={on}
              onClick={() => setActive(t.id)}
              style={{
                padding: '0 var(--sp-4)', height: '100%',
                background: on ? 'var(--bg-panel)' : 'transparent',
                border: 'none',
                borderBottom: `2px solid ${on ? 'var(--accent)' : 'transparent'}`,
                color: on ? 'var(--text-primary)' : 'var(--text-muted)',
                fontSize: 'var(--fs-xs)', fontWeight: on ? 600 : 500,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {t.label}
              {count > 0 && (
                <span style={{
                  marginLeft: 'var(--sp-2)', padding: '0 5px',
                  background: 'var(--bg-active)', borderRadius: 8,
                  fontSize: 'var(--fs-2xs)', color: 'var(--text-secondary)',
                }}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {active === 'positions'  && <PositionsTable rows={positions} />}
        {active === 'orders'     && <OrdersTable rows={orders} />}
        {active === 'executions' && <ExecutionsTable />}
        {active === 'timesales'  && <TimeAndSales />}
        {active === 'depth'      && <MarketDepth />}
        {active === 'strategies' && <StrategiesTable />}
      </div>
    </section>
  )
}

function Empty({ label }: { label: string }) {
  return (
    <div style={{
      display: 'grid', placeItems: 'center', height: '100%',
      color: 'var(--text-muted)', fontSize: 'var(--fs-sm)',
    }}>
      No {label.toLowerCase()} to display
    </div>
  )
}

const th: React.CSSProperties = {
  padding: 'var(--sp-1) var(--sp-3)', textAlign: 'left',
  fontSize: 'var(--fs-2xs)', fontWeight: 600, letterSpacing: '0.05em',
  textTransform: 'uppercase', color: 'var(--text-muted)',
  borderBottom: '1px solid var(--border)',
  position: 'sticky', top: 0, background: 'var(--bg-panel)', zIndex: 1,
  whiteSpace: 'nowrap',
}

const td: React.CSSProperties = {
  padding: '0 var(--sp-3)', height: 'var(--row-h)',
  fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
}

function PositionsTable({ rows }: { rows: Position[] }) {
  const totals = useMemo(() => {
    let pnl = 0, exposure = 0
    for (const r of rows) {
      pnl += Number(r.total_pnl) || 0
      exposure += Math.abs(Number(r.exposure) || 0)
    }
    return { pnl, exposure }
  }, [rows])

  if (rows.length === 0) return <Empty label="Positions" />

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Symbol</th>
          <th style={th}>Exch</th>
          <th style={th}>Product</th>
          <th style={{ ...th, textAlign: 'right' }}>Qty</th>
          <th style={{ ...th, textAlign: 'right' }}>Avg</th>
          <th style={{ ...th, textAlign: 'right' }}>LTP</th>
          <th style={{ ...th, textAlign: 'right' }}>Unrealised</th>
          <th style={{ ...th, textAlign: 'right' }}>Realised</th>
          <th style={{ ...th, textAlign: 'right' }}>Total P&amp;L</th>
          <th style={{ ...th, textAlign: 'right' }}>Exposure</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => {
          const dir = direction(p.total_pnl)
          const color = dir === 'up' ? 'var(--profit)'
                      : dir === 'down' ? 'var(--loss)' : 'var(--flat)'
          const long = Number(p.quantity) > 0
          return (
            <tr key={p.id} style={{ cursor: 'default' }}>
              <td style={{ ...td, fontWeight: 600 }}>{p.symbol ?? p.instrument_id}</td>
              <td style={{ ...td, color: 'var(--text-secondary)' }}>{p.exchange}</td>
              <td style={{ ...td, color: 'var(--text-secondary)' }}>{p.product}</td>
              <td className="num" style={{
                ...td, textAlign: 'right',
                color: long ? 'var(--buy)' : 'var(--sell)',
              }}>
                {/* Direction is stated, not just coloured. */}
                {long ? 'L' : 'S'} {fmtQty(Math.abs(Number(p.quantity)))}
              </td>
              <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtPrice(p.avg_price)}</td>
              <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtPrice(p.last_price)}</td>
              <td className="num" style={{ ...td, textAlign: 'right', color }}>{fmtSigned(p.unrealized_pnl)}</td>
              <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtSigned(p.realized_pnl)}</td>
              <td className="num" style={{ ...td, textAlign: 'right', color, fontWeight: 600 }}>
                {fmtSigned(p.total_pnl)}
              </td>
              <td className="num" style={{ ...td, textAlign: 'right', color: 'var(--text-secondary)' }}>
                {fmtMoney(p.exposure, p.currency, true)}
              </td>
            </tr>
          )
        })}
      </tbody>
      <tfoot>
        <tr style={{ background: 'var(--bg-raised)', fontWeight: 600 }}>
          <td style={{ ...td, borderBottom: 'none' }} colSpan={8}>Total</td>
          <td className="num" style={{
            ...td, textAlign: 'right', borderBottom: 'none',
            color: totals.pnl >= 0 ? 'var(--profit)' : 'var(--loss)',
          }}>
            {fmtSigned(totals.pnl)}
          </td>
          <td className="num" style={{ ...td, textAlign: 'right', borderBottom: 'none' }}>
            {fmtMoney(totals.exposure, 'INR', true)}
          </td>
        </tr>
      </tfoot>
    </table>
  )
}

function OrdersTable({ rows }: { rows: Order[] }) {
  if (rows.length === 0) return <Empty label="Orders" />

  const statusColor = (s: Order['status']) => {
    if (s === 'FILLED') return 'var(--ok)'
    if (s === 'REJECTED' || s === 'EXPIRED') return 'var(--danger)'
    if (s === 'CANCELLED') return 'var(--text-muted)'
    if (s === 'PARTIALLY_FILLED') return 'var(--warn)'
    return 'var(--info)'
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Time</th>
          <th style={th}>Symbol</th>
          <th style={th}>Side</th>
          <th style={th}>Type</th>
          <th style={{ ...th, textAlign: 'right' }}>Qty</th>
          <th style={{ ...th, textAlign: 'right' }}>Filled</th>
          <th style={{ ...th, textAlign: 'right' }}>Price</th>
          <th style={{ ...th, textAlign: 'right' }}>Avg Fill</th>
          <th style={th}>Status</th>
          <th style={th}>Mode</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id}>
            <td className="num" style={{ ...td, color: 'var(--text-secondary)' }}>
              {fmtTime(o.created_at)}
            </td>
            <td style={{ ...td, fontWeight: 600 }}>{o.symbol ?? o.instrument_id}</td>
            <td style={{
              ...td, fontWeight: 700,
              color: o.side === 'BUY' ? 'var(--buy)' : 'var(--sell)',
            }}>
              {o.side}
            </td>
            <td style={{ ...td, color: 'var(--text-secondary)' }}>{o.order_type}</td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtQty(o.quantity)}</td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtQty(o.filled_quantity)}</td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtPrice(o.price)}</td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtPrice(o.avg_fill_price)}</td>
            <td style={{ ...td, color: statusColor(o.status), fontSize: 'var(--fs-xs)' }}>
              {o.status.replace(/_/g, ' ')}
              {o.reject_code && (
                <span title={o.reject_reason ?? ''} style={{ marginLeft: 4, opacity: 0.8 }}>
                  ({o.reject_code})
                </span>
              )}
            </td>
            <td style={{ ...td, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
              {o.trading_mode}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
